import CONFIG from '../config/config';

const localeToCurrencyMap: Record<string, string> = {
  // European countries
  'AT': 'EUR', // Austria
  'BE': 'EUR', // Belgium
  'DE': 'EUR', // Germany
  'ES': 'EUR', // Spain
  'FR': 'EUR', // France
  'IT': 'EUR', // Italy
  'NL': 'EUR', // Netherlands
  'PT': 'EUR', // Portugal
  'IE': 'EUR', // Ireland
  'GR': 'EUR', // Greece
  'FI': 'EUR', // Finland
  'LU': 'EUR', // Luxembourg
  'PL': 'PLN', // Poland
  'GB': 'GBP', // United Kingdom
  'UK': 'GBP', // United Kingdom
  'CH': 'CHF', // Switzerland
  'DK': 'DKK', // Denmark
  'NO': 'NOK', // Norway
  'UA': 'UAH', // Ukraine
  'IL': 'ILS', // Israel
  'KR': 'KRW', // South Korea
  'SG': 'SGD', // Singapore
  'AU': 'AUD', // Australia
  'CA': 'CAD', // Canada
  'US': 'USD', // United States
};

/**
 * Detects the most appropriate currency based on the device's locale
 * @returns A supported currency code, or the configured default currency as fallback
 */
export function detectCurrencyFromLocale(): string {
  try {
    // Get locale from browser
    const locale = navigator.language || (navigator as any).userLanguage || 'en-US';
    
    // Extract region code from locale (e.g., 'en-US' -> 'US', 'pl-PL' -> 'PL')
    const regionMatch = locale.match(/-([A-Z]{2})$/i);
    if (regionMatch) {
      const regionCode = regionMatch[1].toUpperCase();
      const currency = localeToCurrencyMap[regionCode];
      if (currency && CONFIG.SUPPORTED_CURRENCIES.includes(currency)) {
        return currency;
      }
    }
    
    // Try to extract language code as fallback (e.g., 'pl' from 'pl-PL')
    const languageCode = locale.split('-')[0].toUpperCase();
    const currencyFromLanguage = localeToCurrencyMap[languageCode];
    if (currencyFromLanguage && CONFIG.SUPPORTED_CURRENCIES.includes(currencyFromLanguage)) {
      return currencyFromLanguage;
    }
    
  } catch (error) {
    console.warn('Failed to detect currency from locale:', error);
  }
  
  return CONFIG.DEFAULT_CURRENCY;
}

/**
 * Determines the default currency using the priority logic:
 * 1. Use provided currency if available
 * 2. Use tenant currency if user is logged in
 * 3. Detect from device locale
 * 4. Fallback to configured default currency
 * 
 * @param providedCurrency - Currency from props (can be empty/undefined)
 * @param tenantCurrency - Currency from tenant settings (can be undefined)
 * @param isLoggedIn - Whether user is logged in
 * @returns The determined currency code
 */
export function getDefaultCurrency(
  providedCurrency?: string | null,
  tenantCurrency?: string | null,
  isLoggedIn?: boolean
): string {
  if (providedCurrency && providedCurrency.trim() !== '' && CONFIG.SUPPORTED_CURRENCIES.includes(providedCurrency)) {
    return providedCurrency;
  }
  
  if (isLoggedIn && tenantCurrency && CONFIG.SUPPORTED_CURRENCIES.includes(tenantCurrency)) {
    return tenantCurrency;
  }
  
  const detectedCurrency = detectCurrencyFromLocale();
  if (detectedCurrency && CONFIG.SUPPORTED_CURRENCIES.includes(detectedCurrency)) {
    return detectedCurrency;
  }
  
  return CONFIG.DEFAULT_CURRENCY;
}

